import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LogoWhite from "../../assets/logo-2mm-blanc.png";
import LanguageSwitcher from "../LanguageSwitcher";
import ShellIcon from "./ShellIcon";
import { findCurrentItem, ShellNavGroup, ShellNavItem } from "./proNav";
import { useLogout } from "./useLogout";

// Barre latérale de l'espace professionnel : celle de stats.2minutesmax.com (demande de Nicolas du
// 19/10), structurée par groupe comme la maquette v3. Sur mobile, la même barre sert de tiroir et
// reprend en bas le guide, la langue et la déconnexion de la barre du haut.

const NavItem = ({
  item,
  active,
  onNavigate,
}: {
  item: ShellNavItem;
  /** Rubrique du fil d'Ariane, allumée aussi sur ses pages hors URL (alsoMatches). */
  active: boolean;
  onNavigate(): void;
}) =>
  item.otherApp ? (
    <a className="nav-a" href={item.to}>
      <ShellIcon name={item.icon} />
      <span>{item.label}</span>
      {!!item.badge && <span className="nav-a_n">{item.badge}</span>}
    </a>
  ) : item.external ? (
    <a
      className="nav-a"
      href={item.to}
      target="_blank"
      rel="noreferrer"
      onClick={onNavigate}
    >
      <ShellIcon name={item.icon} />
      <span>{item.label}</span>
      <ShellIcon name="external" className="nav-a_ext" />
    </a>
  ) : (
    <NavLink
      className={({ isActive }) =>
        isActive || active ? "nav-a active" : "nav-a"
      }
      to={item.to}
      end={item.end}
      onClick={onNavigate}
    >
      <ShellIcon name={item.icon} />
      <span>{item.label}</span>
    </NavLink>
  );

// Groupes repliables : état choisi par l'utilisateur, gardé dans le navigateur. Même clé que notre SPA /stats/
// (2minutesmax/apps/web-dashboard/src/fusion/FusionShell.tsx, même origine) pour que le menu ne change pas
// d'aspect en passant de l'une à l'autre.
const NAV_GROUPS_KEY = "2mm.navGroups";

function readOpenGroups(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(NAV_GROUPS_KEY) ?? "{}");
  } catch {
    return {};
  }
}

const ProSidebar = ({
  organizationName,
  planLabel,
  planVariant,
  groups,
  guideLink,
  onNavigate,
}: {
  organizationName: string;
  planLabel: string | null;
  planVariant: "free" | "pro" | "plus";
  groups: ShellNavGroup[];
  guideLink: string | null;
  onNavigate(): void;
}) => {
  const { t } = useTranslation(["common"]);
  const logout = useLogout();
  const home = groups[0]?.items[0]?.to ?? "/organization";
  const { pathname } = useLocation();
  const current = findCurrentItem(groups, pathname);
  const [openGroups, setOpenGroups] = useState(readOpenGroups);
  // Le groupe de la page affichée s'ouvre à l'arrivée, mais reste repliable ensuite (Nicolas, 01/11 : le
  // groupe PRO + ne se refermait plus).
  const isOpen = (group: ShellNavGroup) =>
    !group.id ||
    (openGroups[group.id] ??
      (!group.closedByDefault || current?.group === group));
  const currentGroupId = current?.group.id;
  useEffect(() => {
    if (currentGroupId)
      setOpenGroups((prev) =>
        prev[currentGroupId] === false
          ? { ...prev, [currentGroupId]: true }
          : prev,
      );
  }, [pathname, currentGroupId]);
  const toggle = (group: ShellNavGroup) => {
    const next = { ...openGroups, [group.id!]: !isOpen(group) };
    setOpenGroups(next);
    try {
      localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify(next));
    } catch {
      // navigation privée : l'état reste valable pour la page en cours
    }
  };

  return (
    <nav className="side" aria-label={t("common:shell.MAIN_NAV")}>
      <button
        type="button"
        className="iconbtn side_close"
        onClick={onNavigate}
        aria-label={t("common:shell.CLOSE_MENU")}
      >
        <ShellIcon name="close" />
      </button>
      <NavLink className="side_logo" to={home} onClick={onNavigate}>
        <img src={LogoWhite} alt="2 Minutes Max" />
      </NavLink>
      <div className="side_welcome">
        <div className="side_welcome-w">{t("common:sidebar.WELCOME")}</div>
        <div className="side_welcome-o">{organizationName}</div>
        {planLabel && (
          <>
            <div className="side_welcome-w --plan">
              {t("common:shell.YOUR_PLAN")}
            </div>
            <span className={`side_plan --${planVariant}`}>{planLabel}</span>
          </>
        )}
      </div>
      <div className="side_scroll">
        {groups.map((group, i) => (
          <div
            key={group.title ?? i}
            className={group.separated ? "side_grp --sep" : "side_grp"}
          >
            {group.title &&
              (group.id ? (
                <button
                  type="button"
                  className="side_grp-h --toggle"
                  aria-expanded={isOpen(group)}
                  onClick={() => toggle(group)}
                >
                  {group.title}
                  {group.badge && (
                    <span className="side_badge">{group.badge}</span>
                  )}
                  <span className="side_caret" aria-hidden="true" />
                </button>
              ) : (
                <div className="side_grp-h">
                  {group.title}
                  {group.badge && (
                    <span className="side_badge">{group.badge}</span>
                  )}
                </div>
              ))}
            {isOpen(group) &&
              group.items.map((item) => (
                <NavItem
                  key={item.to}
                  item={item}
                  active={current?.item === item}
                  onNavigate={onNavigate}
                />
              ))}
          </div>
        ))}
        <div className="side_grp --sep --mobile-only">
          {guideLink && (
            <a
              className="nav-a"
              href={guideLink}
              target="_blank"
              rel="noreferrer"
            >
              <ShellIcon name="book" />
              <span>{t("common:header.RECRUITER_GUIDE")}</span>
              <ShellIcon name="external" className="nav-a_ext" />
            </a>
          )}
          <div className="nav-a --lang">
            <LanguageSwitcher className="--light" />
          </div>
          <button type="button" className="nav-a" onClick={logout}>
            <ShellIcon name="logout" />
            <span>{t("common:sidebar.buttons.LOGOUT")}</span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default ProSidebar;
